import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, App, Button, Checkbox, Collapse, Drawer, Flex, Form, Input, Popconfirm, Tag, Typography,
} from 'antd';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import {
  createRole, deleteRole, fetchPermissionCatalogue, fetchRoles, updateRole,
  type PermissionOption, type RoleRow,
} from './api';

const { Text } = Typography;

/**
 * Roles as editable bundles of permissions (CLAUDE.md §11).
 *
 * The most dangerous screen in the product: someone can remove their own
 * ability to manage roles, and nobody could then restore it. The API refuses
 * that specific case, and this screen warns before the save rather than after
 * the refusal.
 *
 * Permissions are grouped by module because a flat list of eighty codes is not
 * something anyone can reason about. The grouping is the module prefix —
 * `parts.issue` under `parts` — which is also how the codes are organised in
 * the registry, so the screen and the source agree.
 */
export function RolesPage(): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [form] = Form.useForm();

  const roles = useQuery({ queryKey: ['iam', 'roles'], queryFn: fetchRoles });
  const catalogue = useQuery({ queryKey: ['iam', 'permissions'], queryFn: fetchPermissionCatalogue });

  const grouped = useMemo(() => {
    const byGroup = new Map<string, PermissionOption[]>();
    for (const p of catalogue.data ?? []) {
      const list = byGroup.get(p.group) ?? [];
      list.push(p);
      byGroup.set(p.group, list);
    }
    return [...byGroup.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalogue.data]);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['iam'] });
  };

  // The API's refusal messages explain exactly what would have happened —
  // rewording them here would lose that.
  const fail = (e: unknown): void => {
    message.error(e instanceof ApiError ? e.message : t('iam.genericError'));
  };

  const create = useMutation({
    mutationFn: createRole,
    onSuccess: (role) => {
      message.success(t('iam.roleCreated', { name: role.name }));
      closeDrawers();
      refresh();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: ({ uid, input }: { uid: string; input: { name?: string; permissions?: string[] } }) =>
      updateRole(uid, input),
    onSuccess: () => {
      message.success(t('iam.roleSaved'));
      closeDrawers();
      refresh();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      message.success(t('iam.roleDeleted'));
      refresh();
    },
    onError: fail,
  });

  function closeDrawers(): void {
    setEditing(null);
    setCreating(false);
    setSelected([]);
    form.resetFields();
  }

  const toggleGroup = (codes: string[], checked: boolean): void => {
    setSelected((prev) =>
      checked ? [...new Set([...prev, ...codes])] : prev.filter((c) => !codes.includes(c)),
    );
  };

  // Losing this is what the API refuses. Warned here, before the save.
  const losesRoleManage = editing !== null
    && editing.permissions.includes('iam.role_manage')
    && !selected.includes('iam.role_manage');

  const picker = (
    <Flex vertical gap={8}>
      <Flex justify="space-between" align="center">
        <Text type="secondary">{t('iam.selectedCount', { count: selected.length })}</Text>
        <Button size="small" onClick={() => { setSelected([]); }}>
          {t('iam.clearAll')}
        </Button>
      </Flex>

      {losesRoleManage && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={t('iam.lockoutWarningTitle')}
          description={t('iam.lockoutWarningBody')}
        />
      )}

      <Collapse
        size="small"
        items={grouped.map(([group, permissions]) => {
          const codes = permissions.map((p) => p.code);
          const chosen = codes.filter((c) => selected.includes(c));
          return {
            key: group,
            label: (
              <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                <Text strong>{group}</Text>
                <Tag color={chosen.length > 0 ? 'processing' : 'default'}>
                  {chosen.length}/{codes.length}
                </Tag>
              </Flex>
            ),
            children: (
              <Flex vertical gap={6}>
                <Checkbox
                  indeterminate={chosen.length > 0 && chosen.length < codes.length}
                  checked={chosen.length === codes.length}
                  onChange={(e) => { toggleGroup(codes, e.target.checked); }}
                >
                  <Text type="secondary">{t('iam.selectAllIn', { group })}</Text>
                </Checkbox>
                {permissions.map((p) => (
                  <Checkbox
                    key={p.code}
                    checked={selected.includes(p.code)}
                    onChange={(e) => { toggleGroup([p.code], e.target.checked); }}
                  >
                    {/* The action is what a person reads; the full code is
                        shown because it is what appears in an audit row. */}
                    {p.action} <Text type="secondary" className="tabular" style={{ fontSize: 11 }}>{p.code}</Text>
                  </Checkbox>
                ))}
              </Flex>
            ),
          };
        })}
      />
    </Flex>
  );

  return (
    <>
      <ListPage<RoleRow>
        title={t('iam.roles')}
        loading={roles.isLoading}
        emptyDescription={t('iam.noRolesYet')}
        rows={roles.data ?? []}
        rowKey={(row) => row.uid}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setCreating(true); setSelected([]); }}>
            {t('iam.addRole')}
          </Button>
        }
        onRowClick={(row) => {
          setEditing(row);
          setSelected(row.permissions);
          form.setFieldsValue({ name: row.name });
        }}
        columns={[
          {
            title: t('iam.roleCode'),
            dataIndex: 'code',
            width: 200,
            render: (code: string, row: RoleRow) => (
              <Flex gap={6} align="center">
                <span className="tabular">{code}</span>
                {row.isSystem && <Tag>{t('iam.builtIn')}</Tag>}
              </Flex>
            ),
          },
          { title: t('iam.name'), dataIndex: 'name', width: 220 },
          {
            title: t('iam.people'),
            dataIndex: 'userCount',
            width: 100,
            align: 'right' as const,
            render: (n: number) => <span className="tabular">{n}</span>,
          },
          {
            title: t('iam.permissionCount'),
            width: 140,
            align: 'right' as const,
            render: (_: unknown, row: RoleRow) => (
              <span className="tabular">{row.permissions.length}</span>
            ),
          },
          {
            title: '',
            width: 110,
            render: (_: unknown, row: RoleRow) => (
              <Popconfirm
                title={t('iam.deleteRole')}
                description={t('iam.deleteRoleConfirm', { name: row.name })}
                okText={t('iam.delete')}
                okButtonProps={{ danger: true }}
                onPopupClick={(e) => { e.stopPropagation(); }}
                onConfirm={() => { remove.mutate(row.uid); }}
                disabled={row.userCount > 0}
              >
                <Button
                  size="small"
                  danger
                  // A role with people in it cannot be deleted — the API
                  // refuses too, but disabling it explains why without a
                  // failed request.
                  disabled={row.userCount > 0}
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  {t('iam.delete')}
                </Button>
              </Popconfirm>
            ),
          },
        ]}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center">
              <Text strong className="tabular">{row.code}</Text>
              <Tag>{t('iam.peopleCount', { count: row.userCount })}</Tag>
            </Flex>
            <Text>{row.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('iam.permissionsCount', { count: row.permissions.length })}
            </Text>
          </Flex>
        )}
      />

      <Drawer
        className="nks-action-drawer"
        width={520}
        open={creating || editing !== null}
        title={editing !== null ? editing.name : t('iam.addRole')}
        onClose={closeDrawers}
        extra={
          <Button
            type="primary"
            danger={losesRoleManage}
            loading={create.isPending || update.isPending}
            onClick={() => {
              void form.validateFields().then((values: { code?: string; name: string }) => {
                if (editing !== null) {
                  update.mutate({ uid: editing.uid, input: { name: values.name, permissions: selected } });
                } else {
                  create.mutate({ code: values.code ?? '', name: values.name, permissions: selected });
                }
              });
            }}
          >
            {t('iam.save')}
          </Button>
        }
      >
        <Flex vertical gap={16}>
          <Form form={form} layout="vertical">
            {editing === null && (
              <Form.Item
                name="code"
                label={t('iam.roleCode')}
                rules={[{ required: true }]}
                extra={t('iam.roleCodeHint')}
              >
                <Input size="large" autoFocus />
              </Form.Item>
            )}
            <Form.Item name="name" label={t('iam.name')} rules={[{ required: true }]}>
              <Input size="large" />
            </Form.Item>
          </Form>

          {editing?.isSystem === true && (
            <Alert
              type="info"
              showIcon
              message={t('iam.builtInRole')}
              description={t('iam.builtInRoleBody')}
            />
          )}

          {picker}
        </Flex>
      </Drawer>
    </>
  );
}
